'use client';
import { useEffect, useRef } from 'react';

import { loadScript } from '@monoimin/utils/helpers';
import { PROJECT_SLUG } from '@monoimin/constants';
import { useIntersectionObserver } from '@monoimin/hooks';

import type {
  TrackProps,
  TrackViewProps,
  TrackMouseProps,
  AllProperties,
} from './types';

export const RETRY_DELAY = 2000;

export const scriptLoading = { renta: false };

export const isRentaLoaded = (): boolean =>
  window.renta && typeof window.renta.track === 'function';

export const handleRentaTracking = (
  item: string,
  action: string,
  payload: Record<string, any>
): void => {
  const rentaTrack = () => {
    scriptLoading.renta = false;
    window.renta?.track(`${item} ${action}`, payload);
  };

  if (isRentaLoaded()) {
    rentaTrack();
  } else {
    console.error('rentaTrack: window.renta is not defined');

    if (scriptLoading.renta) {
      console.log('script is loading, queue event');
      setTimeout(rentaTrack, RETRY_DELAY);
    } else {
      scriptLoading.renta = true;
      loadScript({
        src: '/scripts/renta.js',
        id: 'renta-backup',
        onLoad: rentaTrack,
        removeExisted: true,
      });
    }
  }
};

export const analyticsTrack = ({ item, action, properties }: TrackProps) => {
  const payload = {
    ...properties,
  };

  try {
    if (PROJECT_SLUG === 'monoimin') {
      handleRentaTracking(item, action, payload);
    }
  } catch (error: any) {
    console.error(`analyticsTrack`, payload, error);
  }
};

export const TrackView = ({ item, action, properties }: TrackViewProps) => {
  const ref = useRef<HTMLSpanElement | null>(null);

  const entry = useIntersectionObserver(ref, {
    threshold: 0,
    freezeOnceVisible: true,
  });

  useEffect(() => {
    if (!ref.current || !entry?.isIntersecting) {
      return;
    }

    if (entry?.isIntersecting) {
      analyticsTrack({ item, action, properties });
    }
  }, [ref.current, entry?.isIntersecting]);

  return <span ref={ref} />;
};

export const TrackMouse = ({
  item,
  action,
  properties,
  className,
  children,
  as = 'div',
}: TrackMouseProps) => {
  const Elem = as;
  return (
    <Elem
      onClick={() => {
        analyticsTrack({ item, action, properties });
      }}
      className={className}
    >
      {children}
    </Elem>
  );
};

type TrackAllProps = Omit<TrackMouseProps, 'action'>;

export const TrackAll = ({
  properties,
  className,
  children,
  item,
  as = 'div',
}: TrackAllProps) => {
  return (
    <TrackMouse
      as={as}
      item={item}
      action='click'
      properties={properties}
      className={className}
    >
      <TrackView item={item} action='view' properties={properties} />
      {children}
    </TrackMouse>
  );
};

interface OptionalTrackerProps extends Omit<TrackAllProps, 'properties'> {
  enabled: boolean;
  properties: AllProperties | null;
}
export const OptionalTracker = ({
  properties,
  enabled,
  className,
  children,
  item,
  as = 'div',
}: OptionalTrackerProps) => {
  if (!enabled || !properties) {
    const Elem = as;
    return <Elem className={className}>{children}</Elem>;
  }

  return (
    <TrackAll properties={properties} className={className} item={item} as={as}>
      {children}
    </TrackAll>
  );
};
